import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Box,
  Typography,
  Stack,
  Divider,
} from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  KeyboardArrowRight,
  KeyboardArrowDown,
  Dashboard,
  Category,
  AdminPanelSettings,
  People,
  ShoppingCart,
  LocalOffer,
  ContactMail,
  EventNote,
  AccountBalanceWallet,
  VideoLibrary,
  School,
  MailOutline,
  Description,
} from '@mui/icons-material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import ChatIcon from '@mui/icons-material/Chat';
import BarChartIcon from '@mui/icons-material/BarChart';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';

const SideMenu = () => {
  const location = useLocation();
  const { user } = useAuth();
  const userRole = Number(user?.role);

  const [openClients, setOpenClients] = useState(false);
  const [openCommands, setOpenCommands] = useState(false);
  const [openContact, setOpenContact] = useState(false);
  const [openReservations, setOpenReservations] = useState(false);
  const [openOffres, setOpenOffres] = useState(false);

  const menuIcons = {
    dashboard: <Dashboard sx={{ fontSize: 20 }} />,
    categories: <Category sx={{ fontSize: 20 }} />,
    admin: <AdminPanelSettings sx={{ fontSize: 20 }} />,
    clients: <People sx={{ fontSize: 20 }} />,
    commandes: <ShoppingCart sx={{ fontSize: 20 }} />,
    offres: <LocalOffer sx={{ fontSize: 20 }} />,
    contact: <ContactMail sx={{ fontSize: 20 }} />,
    reservations: <EventNote sx={{ fontSize: 20 }} />,
    conversations: <ChatIcon sx={{ fontSize: 20 }} />,
    wallet: <AccountBalanceWallet sx={{ fontSize: 20 }} />,
    videos: <VideoLibrary sx={{ fontSize: 20 }} />,
    ateliers: <School sx={{ fontSize: 20 }} />,
    guideVideos: <MenuBookIcon sx={{ fontSize: 20 }} />,
    statistiques: <BarChartIcon sx={{ fontSize: 20 }} />,
    emailsHistory: <MailOutline sx={{ fontSize: 20 }} />,
    patients: <MedicalServicesIcon sx={{ fontSize: 20 }} />,
    aidants: <SupervisorAccountIcon sx={{ fontSize: 20 }} />,
    validation: <VerifiedUserIcon sx={{ fontSize: 20 }} />,
    comptesRendus: <Description sx={{ fontSize: 20 }} />,
  };

  useEffect(() => {
    if (location.pathname.includes('/clients')) setOpenClients(true);
    if (location.pathname.includes('/commandes')) setOpenCommands(true);
    if (location.pathname.includes('/offres')) setOpenOffres(true);
    if (location.pathname.includes('/reservations')) setOpenReservations(true);
  }, [location.pathname]);

  // Redirection personnalisée du Dashboard selon le rôle
  const getDashboardPath = useCallback(() => {
    if (!user) return '/login';
    if (userRole === 3) return '/dashboard-intervenant';
    if (userRole === 4) return '/dashboard-praticien';
    return '/dashboard-user';
  }, [user, userRole]);

  const menuItems = useMemo(() => {
    const items = [];

    // 1. Dashboard commun
    items.push({
      text: 'Dashboard',
      path: getDashboardPath(),
      icon: menuIcons.dashboard,
    });

    // --- UNIQUEMENT POUR LE SUPER ADMIN (Rôle 1) ---
    if (userRole === 1) {
      items.push({
        text: 'Validation en attente',
        path: '/validation-pending',
        icon: menuIcons.validation || <AdminPanelSettings sx={{ fontSize: 20 }} />,
      });
    }

    // --- MENU POUR LES ADMINS (1, 2) ---
    if ([1, 2].includes(userRole)) {
      items.push({
        text: 'Gestion des équipes',
        path: '/administration',
        icon: menuIcons.admin,
      });

      items.push({
        text: ' Clients',
        hasSubMenu: true,
        isOpen: openClients,
        toggle: () => setOpenClients(!openClients),
        icon: menuIcons.clients,
      });

      if (openClients) {
        items.push(
          { text: 'Liste Clients', path: '/clients', isSubItem: true },
          { text: 'Pack Jetons', path: '/clients-pack-jetons', isSubItem: true },
          { text: 'Clients Abonnés', path: '/clients-abonnes', isSubItem: true }
        );
      }

      items.push({
        text: 'Patients',
        path: '/patients',
        icon: menuIcons.patients,
      });

      items.push({
        text: 'Aidants',
        path: '/aidants',
        icon: menuIcons.aidants,
      });

      items.push(
        { text: 'Abonnements', path: '/abonnements', icon: menuIcons.commandes },
        { text: 'Packs jetons', path: '/packs-jetons', icon: menuIcons.wallet }
      );
    }

    // --- MENU POUR LES PRATICIENS (Rôle 4) ---
    if (userRole === 4) {
      
      
      // Réservations avec sous-menu pour le rôle 4
      items.push({
        text: 'Réservations',
        hasSubMenu: true,
        isOpen: openReservations,
        toggle: () => setOpenReservations(!openReservations),
        icon: menuIcons.reservations,
      });

      if (openReservations) {
        items.push(
          { text: 'Réservations', path: '/reservations', isSubItem: true },
          { text: 'Historique', path: '/reservations/historique', isSubItem: true }
        );
      }

      
    }

    // --- COMPTES RENDUS (Accessible à tous les rôles 1, 2, 3, 4) ---
    if ([1, 2, 3, 4].includes(userRole)) {
      items.push({
        text: 'Comptes Rendus',
        path: '/comptes-rendus',
        icon: menuIcons.comptesRendus,
      });
    }

    // --- ÉLÉMENTS PARTAGÉS OU SPÉCIFIQUES ---
    if ([1, 2].includes(userRole)) {
      items.push({
        text: 'Catégories',
        path: '/categories',
        icon: menuIcons.categories,
      });
    }
    if ([1, 2, 3,4].includes(userRole)) {

    items.push({
      text: 'Soins individuels',
      path: '/offres',
      icon: menuIcons.offres,
    });
  }
    if ([1, 2, 3, 4].includes(userRole)) {
      items.push({
        text: 'Ateliers',
        path: '/ateliers',
        icon: menuIcons.ateliers,
      });
    }

   // Réservations pour les rôles 1, 2, 3 (le rôle 4 est déjà géré séparément)
 if (![4].includes(userRole)) {
      items.push({
        text: 'Gestion Réservations',
        hasSubMenu: true,
        isOpen: openReservations,
        toggle: () => setOpenReservations(!openReservations),
        icon: menuIcons.reservations,
      });

      if (openReservations) {
        items.push(
          { text: 'Planning', path: '/reservations', isSubItem: true },
          { text: 'Historique', path: '/reservations/historique', isSubItem: true }
        );
      }
    }

    if ([1, 2, 3 ,4].includes(userRole)) {
      items.push({
        text: 'Commandes',
        hasSubMenu: true,
        isOpen: openCommands,
        toggle: () => setOpenCommands(!openCommands),
        icon: menuIcons.commandes,
      });

      if (openCommands) {
        items.push(
          { text: 'Commandes', path: '/commandes', isSubItem: true },
          { text: 'Factures', path: '/commandes/factures', isSubItem: true },
          { text: 'Factures Intervenant/Praticin', path: '/commandes/factures-honoraires', isSubItem: true }
        );
      }
    }

    if ([1, 2].includes(userRole)) {
      items.push({
        text: 'Statistiques',
        path: '/statistiques',
        icon: menuIcons.statistiques,
      });

      items.push({
        text: 'Historique e-mails',
        path: '/emails-history',
        icon: menuIcons.emailsHistory,
      });
    }
    if ([1, 2].includes(userRole)) {

    items.push({
      text: 'Conversations',
      path: '/conversations',
      icon: menuIcons.conversations,
    });

    items.push({
      text: 'Vidéos',
      path: '/videos',
      icon: menuIcons.videos,
    });
  }
    items.push({
      text: 'Guide vidéos',
      path: '/guide-videos',
      icon: menuIcons.guideVideos,
    });

    if ([1, 2].includes(userRole)) {
      items.push({
        text: 'Contact',
        hasSubMenu: true,
        isOpen: openContact,
        toggle: () => setOpenContact(!openContact),
        icon: menuIcons.contact,
      });

      if (openContact) {
        items.push(
          { text: 'Newsletter', path: '/contact/newsletter', isSubItem: true },
          { text: 'Messages Contact', path: '/contact/messages', isSubItem: true },
          { text: 'Questions AI', path: '/contact/questions-ai', isSubItem: true }
        );
      }
    }

    return items;
  }, [
    userRole,
    openClients,
    openCommands,
    openContact,
    openReservations,
    openOffres,
    getDashboardPath,
  ]);

  return (
    <Box
      sx={{
        height: 'calc(100vh - 120px)',
        margin: '110px 16px 24px 24px',
        backgroundColor: '#fff',
        borderRadius: '24px',
        border: '1px solid #eceef0',
        padding: '24px 16px',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0px 4px 12px rgba(0,0,0,0.03)',
        position: 'sticky',
        top: '110px',
        overflow: 'hidden',
      }}
    >
      {/* HEADER */}
      <Box sx={{ px: 1, mb: 4 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: '4px',
              height: '20px',
              bgcolor: '#4318FF',
              borderRadius: '4px',
            }}
          />
          <Typography
            sx={{
              color: '#2B3674',
              fontWeight: '800',
              fontSize: '12px',
              letterSpacing: '1.5px',
              textTransform: 'uppercase',
            }}
          >
            Menu Principal
          </Typography>
        </Stack>

        <Divider
          sx={{ mt: 2, borderColor: 'rgba(0,0,0,0.05)', width: '35%' }}
        />
      </Box>

      {/* LIST */}
      <List
        sx={{
          p: 0,
          flexGrow: 1,
          overflowY: 'auto',
          pb: 8,
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#E0E0E0',
            borderRadius: '8px',
          },
        }}
      >
        {menuItems.map((item, index) => {
          const isActive = location.pathname === item.path;

          return (
            <ListItem
              key={index}
              component={item.hasSubMenu ? 'div' : Link}
              to={item.hasSubMenu ? undefined : item.path}
              onClick={item.hasSubMenu ? item.toggle : undefined}
              disablePadding
              sx={{
                borderRadius: '12px',
                mb: 0.5,
                cursor: 'pointer',
                transition: '0.2s',
                bgcolor: isActive ? 'rgba(67, 24, 255, 0.05)' : 'transparent',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.03)' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  py: 1.5,
                  px: 2,
                  pl: item.isSubItem ? 5 : 2,
                  gap: 1.5,
                  position: 'relative',
                }}
              >
                {isActive && (
                  <Box
                    sx={{
                      width: '6px',
                      height: '6px',
                      borderRadius: '50%',
                      bgcolor: '#4318FF',
                      position: 'absolute',
                      left: item.isSubItem ? '28px' : '8px',
                    }}
                  />
                )}

                {!item.isSubItem && item.icon && (
                  <Box sx={{ color: '#4318FF', display: 'flex' }}>
                    {item.icon}
                  </Box>
                )}

                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    fontSize: item.isSubItem ? '13px' : '14px',
                    fontWeight: isActive || item.isOpen ? '700' : '500',
                    color: isActive || item.isOpen ? '#2B3674' : '#A3AED0',
                  }}
                />

                {item.hasSubMenu &&
                  (item.isOpen ? (
                    <KeyboardArrowDown
                      sx={{ fontSize: 18, color: '#4318FF' }}
                    />
                  ) : (
                    <KeyboardArrowRight
                      sx={{ fontSize: 18, color: '#A3AED0' }}
                    />
                  ))}
              </Box>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

export default SideMenu;